/**
 * API Client
 * Base configuration for API calls
 */

/// <reference types="vite/client" />

import axios, { AxiosInstance, AxiosError } from 'axios';
import { getPathfulAppUrl } from './runtimeConfig';

// Use relative path - Vite proxy will forward to backend
const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

// Log API URL in development to help debug
if (import.meta.env.DEV) {
  console.log('API Base URL:', API_BASE_URL);
  console.log('VITE_API_URL env var:', import.meta.env.VITE_API_URL);
}

export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Extract a validation error message from the first entry of an errors array
function extractValidationErrorMessage(firstError: any): string {
  if (typeof firstError === 'string') {
    return firstError;
  }
  if (firstError.msg) {
    return firstError.msg;
  }
  if (firstError.message) {
    return firstError.message;
  }
  return `Validation error: ${JSON.stringify(firstError)}`;
}

// Map an HTTP status code to a default error message
function statusErrorMessage(status: number): string {
  switch (status) {
    case 400:
      return 'Bad request. Please check your input and try again.';
    case 403:
      return 'Forbidden. You do not have permission to perform this action.';
    case 404:
      return 'Resource not found.';
    case 500:
      return 'Server error. Please try again later.';
    default:
      return `Server returned ${status}`;
  }
}

// True when the response is an HTML error page rather than the app's JSON error shape —
// e.g. CloudFront/WAF blocking a request before it reaches the app. The app's own error
// responses are always application/json (see middleware/adminAuth.ts, routes/*.ts), so a
// text/html content-type is an unambiguous signal that the request never reached the server.
function isHtmlErrorResponse(contentType: string | undefined): boolean {
  return typeof contentType === 'string' && contentType.toLowerCase().includes('text/html');
}

// Extract an error message from various possible response formats
function extractErrorMessage(errorData: any, status: number, contentType: string | undefined): string {
  if (isHtmlErrorResponse(contentType)) {
    return `Your changes were not saved (HTTP ${status}). The request was blocked before reaching the server. Please try again.`;
  }
  if (errorData.message) {
    return errorData.message;
  }
  if (errorData.error) {
    return errorData.error;
  }
  if (errorData.errors && Array.isArray(errorData.errors) && errorData.errors.length > 0) {
    return extractValidationErrorMessage(errorData.errors[0]);
  }
  if (typeof errorData === 'string') {
    return errorData;
  }
  return statusErrorMessage(status);
}

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response) {
      // Server responded with error status
      console.error('API Error:', error.response.status, error.response.data);
      const errorData = error.response.data as any || {};

      if (error.response.status === 401) {
        const returnUrl = encodeURIComponent(globalThis.location.href)
        globalThis.location.assign(`${getPathfulAppUrl()}/login?page=${returnUrl}`)
        const redirectError = Object.assign(new Error('Redirecting to login...'), { error: 'Redirecting', status: 401 });
        return Promise.reject(redirectError);
      }

      const errorMessage = extractErrorMessage(errorData, error.response.status, error.response.headers['content-type']);

      const responseError = Object.assign(new Error(errorMessage), {
        error: errorData.error || 'API Error',
        status: error.response.status,
        data: errorData,
      });
      return Promise.reject(responseError);
    } else if (error.request) {
      // Request made but no response received
      console.error('Network Error:', error.request);
      console.error('API Base URL:', API_BASE_URL);
      const networkError = Object.assign(
        new Error(`Unable to reach server at ${API_BASE_URL}. Please check your API configuration.`),
        { error: 'Network error' }
      );
      return Promise.reject(networkError);
    } else {
      // Something else happened
      console.error('Error:', error.message);
      const requestError = Object.assign(new Error(error.message), { error: 'Request error' });
      return Promise.reject(requestError);
    }
  }
);

export default apiClient;
